import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const category = useMemo(
    () => (article ? categories.find((c) => c.id === article.category) : null),
    [article],
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <SEOHead title="文章未找到" />
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-8 bg-background-50">
            <div className="max-w-3xl mx-auto text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
                <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
              <Link
                to="/"
                className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
              >
                <i className="ri-arrow-left-line"></i>返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    url: `https://navlink2023.github.io/article/${article.slug}`,
    articleBody: article.content.replace(/<[^>]*>/g, '').substring(0, 500),
    wordCount: article.content.replace(/<[^>]*>/g, '').length,
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://navlink2023.github.io/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '文章', item: `https://navlink2023.github.io/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://navlink2023.github.io/article/${article.slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://navlink2023.github.io/article/${article.slug}`}
        ogType="article"
      />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="Article" data={articleSchema} />

      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-100 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto py-4">
            <Breadcrumb
              items={[
                { label: '网址分类', path: `/category/${article.category}` },
                { label: category?.name || '文章', path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                  {category?.name || article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span><i className="ri-user-line mr-1.5"></i>{article.author}</span>
                <span><i className="ri-calendar-line mr-1.5"></i>{article.publishDate}</span>
              </div>
            </header>

            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-h2:text-lg md:prose-h2:text-xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-sm md:prose-p:text-base prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-950 prose-strong:font-semibold
                [&_h2]:text-lg md:[&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_p]:text-sm md:[&_p]:text-base [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4
                [&_strong]:text-foreground-950 [&_strong]:font-semibold
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group p-4 rounded-xl bg-background-100 hover:bg-background-200/50 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                          {categories.find((c) => c.id === related.category)?.name || related.category}
                        </span>
                        <span className="text-xs text-foreground-400">{related.publishDate}</span>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}